import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment-timezone';
import _isEmpty from 'lodash/isEmpty';
import _range from 'lodash/range';
import _map from 'lodash/map';
import _trim from 'lodash/trim';
//Local files
import { CardItem } from './SuperbillFlows.styled';
import SuperbillAnalysesList from 'components/Analyses/SuperbillAnalyses/List/List';
import SuperbillGoalsList from 'components/Flows/SuperbillGoals/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useSuperbillFlows from 'hooks/useSuperbillFlows';
import useSuperbillAnalyses from 'hooks/useSuperbillAnalyses';
import useSuperbillGoals from 'hooks/useSuperbillGoals';
import { DELETE_SUPERBILL_FLOW } from 'helpers/confirmations';
import { checkEmptyString, checkValidEmail, needToUpdateField } from 'helpers';
import { timezones } from 'helpers/constants';
import { superbillFlowsSelectors } from "features/superbillFlows";

const MIN_DAYS = 0;
const MAX_DAYS = 7;

const SuperbillFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getSuperbillFlow, createSuperbillFlow, updateSuperbillFlow } = useSuperbillFlows();
  const { getSuperbillAnalyses, clearLocalSuperbillAnalyses } = useSuperbillAnalyses();
  const { getSuperbillGoals, clearLocalSuperbillGoals } = useSuperbillGoals();
  const id = useCustomSelector(superbillFlowsSelectors.getSuperbillFlowId);
  const superbillFlow = useCustomSelector(superbillFlowsSelectors.getSuperbillFlowData);
  const fetching = useCustomSelector(superbillFlowsSelectors.superbillFlowIsFetching);
  const [beforeIn, setBeforeIn] = useState('PT0S');
  const [startAt, setStartAt] = useState({ value: '', formattedValue: null, error: '' });
  const [timezone, setTimezone] = useState('US/Eastern');
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [superbillEmail, setSuperbillEmail] = useState({ value: '', error: '' });
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const beforeInArray = _map(_range(MIN_DAYS, MAX_DAYS + 1), day => {
    if (day === 0) {
      return 'PT0S'
    } else {
      return `P${day}D`;
    }
  });

  const handleEntering = (superbill_flow) => {
    const todayDate = moment().startOf('day');
    const startAtFromServer = moment.utc(superbill_flow?.start_at);
    const startAtWithTodayDate = startAtFromServer
      .set({
        year: todayDate.year(),
        month: todayDate.month(),
        date: todayDate.date(),
      })
      .tz(superbill_flow?.time_zone);
    const startAtFinal = startAtWithTodayDate.format('YYYY-MM-DDTHH:mm:ss');
    
    setBeforeIn(superbill_flow?.before_in);
    setStartAt({ value: startAtFinal, formattedValue: new Date(startAtFinal), error: '' });
    setTimezone(superbill_flow?.time_zone);
    setPrompt({ value: superbill_flow?.prompt, error: '' });
    setSuperbillEmail({ value: superbill_flow?.superbill_email, error: '' });
  };
  const handleExited = () => {
    setBeforeIn('PT0S');
    setStartAt({ value: '', formattedValue: null, error: '' });
    setTimezone('US/Eastern');
    setPrompt({ value: '', error: '' });
    setSuperbillEmail({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(startAt.value)) {
      setStartAt(prev => ({ ...prev, error: `Start At can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    if (!checkValidEmail(superbillEmail.value)) {
      setSuperbillEmail(prev => ({ ...prev, error: `Email is invalid` }) );
      hasError = true;
    }
    if (hasError) return;

    const startAtUTC = moment.tz(startAt.value, timezone).utc().format();

    const superbill_flow = {
      ...(needToUpdateField(superbillFlow?.before_in, beforeIn) && { before_in: beforeIn }),
      ...(needToUpdateField(superbillFlow?.start_at, startAtUTC) && { start_at: startAtUTC }),
      ...(needToUpdateField(superbillFlow?.time_zone, timezone) && { time_zone: timezone }),
      ...(needToUpdateField(superbillFlow?.prompt, prompt.value) && { prompt: prompt.value }),
      ...(needToUpdateField(superbillFlow?.superbill_email, superbillEmail.value) && {  superbill_email: superbillEmail.value }),
    };

    if (!_isEmpty(superbill_flow)) {
      if (!!id) {
        updateSuperbillFlow(superbill_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createSuperbillFlow(superbill_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_SUPERBILL_FLOW });
  const fetchSuperbillAnalyses = useCallback(() => {
    getSuperbillAnalyses({ offset: 0 });
  }, [getSuperbillAnalyses]);
  const fetchSuperbillGoals = useCallback(() => {
    getSuperbillGoals({ offset: 0 });
  }, [getSuperbillGoals]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalSuperbillAnalyses();
      clearLocalSuperbillGoals();
    };
  }, [id, clearLocalSuperbillAnalyses, clearLocalSuperbillGoals]);
  useEffect(() => {
    if (firstTimeOpened) {
      getSuperbillFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { superbill_flow } = payload;
          handleEntering(superbill_flow);
          fetchSuperbillAnalyses();
          fetchSuperbillGoals();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getSuperbillFlow, fetchSuperbillAnalyses, fetchSuperbillGoals, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label='Superbill Email'
              margin='dense'
              disabled={fetching || (!!id && !edit)}
              value={superbillEmail.value}
              onChange={({ target: { value } }) => setSuperbillEmail({ value, error: '' })}
              helperText={superbillEmail.error}
              error={!!superbillEmail.error}
            />
            <TextField
              fullWidth
              margin='dense'
              select
              disabled={fetching || (!!id && !edit)}
              label='Days Before'
              value={beforeIn}
              onChange={({ target: { value } }) => setBeforeIn(value)}
            >
              {_map(beforeInArray, n => <MenuItem key={n} value={n}>{n === 'PT0S' ? 'Today' : _trim(n, 'PD')}</MenuItem>)}
            </TextField>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                label="Start at"
                value={startAt.formattedValue}
                disabled={fetching || (!!id && !edit)}
                onChange={(newValue) => {
                  const newDate = !!newValue && newValue?.toString() !== 'Invalid Date' ? newValue : '';
                  const formattedValue = !!newDate ? new Date(newDate) : null;
                  const value = !!newDate ? moment(newDate).format('YYYY-MM-DDTHH:mm:ss') : '';
                  setStartAt({
                    value,
                    formattedValue,
                    error: !newDate ? 'Date is invalid' : '',
                  });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    margin='dense' 
                    helperText={startAt.error} 
                    error={!!startAt.error} 
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              margin='dense'
              select
              label='Timezone'
              value={timezone}
              onChange={({ target: { value } }) => setTimezone(value)}
              disabled={fetching || (!!id && !edit)}
            >
              {timezones.map((tz) => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label='Prompt'
              margin='dense'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <SuperbillGoalsList />
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <SuperbillAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SuperbillFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);